import { Eye, Plus, RefreshCw, Trash2 } from 'lucide-react'
import type { HistoryEntry as HistoryEntryType, HistoryAction } from '@/stores/cardHistoryStore'

interface HistoryEntryProps {
  entry: HistoryEntryType
  expanded?: boolean
  onClick: () => void
  onHoverStart?: () => void
  onHoverEnd?: () => void
}

const ACTION_CONFIG: Record<HistoryAction, { label: string; color: string; icon: typeof Eye }> = {
  viewed: { label: 'Viewed', color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300', icon: Eye },
  added: { label: 'Added', color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400', icon: Plus },
  updated: { label: 'Updated', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400', icon: RefreshCw },
  removed: { label: 'Removed', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400', icon: Trash2 },
}

function formatRelativeTime(timestamp: number): string {
  const diff = Math.floor((Date.now() - timestamp) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

export default function HistoryEntry({ entry, expanded = false, onClick, onHoverStart, onHoverEnd }: HistoryEntryProps) {
  const config = ACTION_CONFIG[entry.action]
  const Icon = config.icon

  const thumbSize = expanded ? 'w-10 h-14' : 'w-8 h-11'

  return (
    <div
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-dark-border/50 transition-colors text-left rounded-md group"
    >
      {/* Card thumbnail - clickable */}
      <button
        onClick={onClick}
        className={`${thumbSize} flex-shrink-0 rounded overflow-hidden bg-gray-100 dark:bg-dark-border`}
      >
        {entry.cardImageUrl ? (
          <img
            src={entry.cardImageUrl}
            alt={entry.cardName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[8px] text-gray-400">
            N/A
          </div>
        )}
      </button>

      {/* Card info - clickable */}
      <button onClick={onClick} className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-1.5">
          <span className={`font-medium text-gray-900 dark:text-dark-text truncate ${expanded ? 'text-sm' : 'text-xs'}`}>
            {entry.cardName}
          </span>
          <span className="text-[10px] text-gray-400 dark:text-dark-muted flex-shrink-0">
            [{entry.setCode}]
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[10px] font-medium ${config.color}`}>
            <Icon className="w-2.5 h-2.5" />
            {config.label}
          </span>
          {entry.action !== 'viewed' && entry.newQuantity !== undefined && (
            <span className="text-[10px] text-gray-400 dark:text-dark-muted">
              {entry.previousQuantity ?? 0} &rarr; {entry.newQuantity}
            </span>
          )}
        </div>
        {entry.collectionName && (
          <span className="text-[10px] text-gray-400 dark:text-dark-muted truncate block">
            {entry.collectionName}
          </span>
        )}
      </button>

      {/* CM/CT quick links + timestamp */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="text-[10px] text-gray-400 dark:text-dark-muted">
          {formatRelativeTime(entry.timestamp)}
        </span>
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {entry.cardmarketUrl && (
            <a
              href={entry.cardmarketUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="w-5 h-3.5 rounded flex items-center justify-center text-[6px] font-bold bg-orange-100 text-orange-600 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-900/50 transition-colors"
              title="Open on Cardmarket"
            >
              CM
            </a>
          )}
          {entry.blueprintId && (
            <a
              href={`https://www.cardtrader.com/cards/${entry.blueprintId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="w-5 h-3.5 rounded flex items-center justify-center text-[6px] font-bold bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 transition-colors"
              title="Open on CardTrader"
            >
              CT
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
